import { config } from "../config";
/**
 * Calls the Cloudflare Worker proxy, which forwards to Roboflow's hosted API
 * with the API key injected server-side. Do NOT put the Roboflow API key in
 * this file or any frontend code.
 */
export class RoboflowClient {
    async detect(image, options = {}) {
        const modelVersion = options.modelVersion ?? config.modelVersion;
        const confidence = options.confidenceThreshold ?? config.confidenceThreshold;
        const url = new URL(config.inferenceProxyUrl, window.location.origin);
        url.searchParams.set("version", String(modelVersion));
        url.searchParams.set("confidence", String(confidence));
        const started = performance.now();
        // Roboflow Hosted API wants the image as base64 text in the body, with
        // Content-Type application/x-www-form-urlencoded. NOT raw binary, NOT
        // multipart. If this changes, you'll see "Malformed base64 input image"
        // from upstream.
        const base64 = await blobToBase64(image);
        const response = await fetch(url.toString(), {
            method: "POST",
            body: base64,
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        });
        if (!response.ok) {
            const body = await response.text().catch(() => "");
            throw new Error(`Inference failed: ${response.status} ${response.statusText} — ${body.slice(0, 200)}`);
        }
        const raw = (await response.json());
        const elapsedMs = performance.now() - started;
        const detections = raw.predictions.map((p) => ({
            x: p.x,
            y: p.y,
            width: p.width,
            height: p.height,
            confidence: p.confidence,
            class: p.class,
            classId: p.class_id,
            ...(p.points ? { points: p.points } : {}),
        }));
        return {
            detections,
            imageWidth: raw.image.width,
            imageHeight: raw.image.height,
            modelVersion,
            elapsedMs,
        };
    }
}
/**
 * Convert a Blob to a base64 string (no data-URL prefix).
 * FileReader handles large blobs without blowing the call stack the way
 * btoa(String.fromCharCode(...bytes)) does.
 */
async function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result !== "string") {
                reject(new Error("FileReader did not return a string"));
                return;
            }
            const commaIdx = result.indexOf(",");
            if (commaIdx < 0) {
                reject(new Error("FileReader result missing data-URL comma"));
                return;
            }
            resolve(result.slice(commaIdx + 1));
        };
        reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
        reader.readAsDataURL(blob);
    });
}
